import {useFixWithCopilot} from '@github-ui/copilot-fix-controls/useFixWithCopilot'
import {useMutation} from '@github-ui/react-query'
import {useActiveRoute} from '../hooks/use-active-route'
import {usePullRequestBasePath, useUnifiedBatch, useUnifiedBatchActions} from '../stores/layout-selectors'
import type {UnifiedBatchItem} from '../stores/slices/unified-batch'

export type ApplyUnifiedBatchFixVariables = {
  /** `true` opens a new PR (`Fix and open pull request`); `false` commits to the branch. */
  createPullRequest: boolean
  /** Free-text additional instructions for Copilot (already trimmed). */
  instructions: string
  /** Selected model id (defaults to `'auto'`). */
  model: string
}

/**
 * Builds the `review_references` payload for a batch. Each batched comment is
 * referenced by its review-comment anchor appended to the PR base path,
 * producing `${basePath}#discussion_r{databaseId}` (e.g.
 * `/owner/repo/pull/N#discussion_r3`). The server resolves each reference by the
 * trailing `#discussion_r{databaseId}` anchor, so the PR-scoped path is
 * sufficient and the `/files` segment is not required — this matches the
 * per-comment Fix flow, which hands off `comment.url` permalinks (themselves of
 * the form `…/pull/N#discussion_r{id}`) that resolve by the same anchor.
 *
 * `UnifiedBatchItem`s carry only identifiers (no URL), and the same comment can
 * appear multiple times in the batch (one entry per fenced suggestion block), so
 * references are de-duplicated by `commentDatabaseId` — the CCA hand-off operates
 * per comment, not per suggestion.
 */
export function buildBatchReviewReferences(items: UnifiedBatchItem[], basePath: string): string[] {
  const seen = new Set<number>()
  const references: string[] = []
  for (const item of items) {
    if (seen.has(item.commentDatabaseId)) continue
    seen.add(item.commentDatabaseId)
    references.push(`${basePath}#discussion_r${item.commentDatabaseId}`)
  }
  return references
}

/**
 * Data layer for the Fix with Copilot tab of the unified batch panel.
 *
 * Hands the batch off to the Copilot cloud agent by reusing the per-comment
 * {@link useFixWithCopilot} submission hook — the unified panel simply sources
 * its `review_references` from the `unifiedBatch` slice (one permalink per
 * comment) instead of a single comment, and the server creates one CCA task for
 * the whole batch.
 *
 * Unlike the Commit data layer, every batched comment is eligible (suggestion or
 * comment-only), so there is no committable filter here — the Fix path is the
 * fallback for batches that contain comments without suggestions.
 *
 * On a successful hand-off the batch is cleared (the cloud agent has taken
 * ownership of the comments), mirroring the Commit tab. Unlike Commit, the panel
 * is left open so the returned `taskUrl` can surface the "View agent session" CTA.
 *
 * Returns the react-query mutation plus `taskUrl`, which {@link useFixWithCopilot}
 * populates from the response so the tab can surface a "View agent session" link.
 */
export function useApplyUnifiedBatchFixWithCopilotMutation() {
  const basePath = usePullRequestBasePath()
  const surface = useActiveRoute()
  const {items} = useUnifiedBatch()
  const {clearBatch, setSubmitting} = useUnifiedBatchActions()
  const {submitFix, taskUrl, resetTaskUrl} = useFixWithCopilot({basePath})

  const mutation = useMutation({
    onMutate: () => {
      setSubmitting(true)
    },
    mutationFn: async ({createPullRequest, instructions, model}: ApplyUnifiedBatchFixVariables) => {
      const reviewReferences = buildBatchReviewReferences(items, basePath)

      if (reviewReferences.length === 0) {
        throw new Error('No comments in batch')
      }

      await submitFix({
        createPullRequest,
        instructions,
        model,
        reviewReferences,
        isBatch: true,
        // The batch sends explicit per-comment references, so this is never a
        // "fix every comment in the PR" request.
        fixAll: false,
        analyticsExtras: {label: 'unified_batch_panel_fix', surface},
      })
    },
    onSuccess: () => {
      clearBatch()
    },
    onSettled: () => {
      setSubmitting(false)
    },
  })

  return {...mutation, taskUrl, resetTaskUrl}
}
